// src/components/DiagramEditor.tsx
import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle
} from "react";
import BpmnJS from "bpmn-js/lib/Modeler";
import { saveAs } from "file-saver";
import {
  CloudArrowUp,
  Braces,
  CheckCircleFill,
  Download,
  ExclamationTriangleFill,
  PencilSquare,
  CloudUpload,
} from "react-bootstrap-icons";

// bpmn-js stylesheets are imported in src/index.tsx so that index.css (which
// overrides the .djs-* chrome) is guaranteed to load after them.

import { CreateAppendAnythingModule } from "bpmn-js-create-append-anything";
import type Canvas from "diagram-js/lib/core/Canvas";

import { Button } from "./ui/Button";
import { useDialogs } from "./ui/dialogs";
import { useRealtime } from "../realtime/RealtimeProvider";
import { PresenceAvatars } from "./PresenceAvatars";
import { cn } from "../lib/cn";

// --- Helper to safely generate Document ID ---
const ensureDocumentId = (jsonString: string) => {
    let data: any = {};
    let isModified = false;
    
    try {
        let sanitized = (jsonString || "{}").trim();
        if (sanitized.startsWith('{')) {
            sanitized = sanitized.replace(/([{,]\s*)([A-Za-z0-9_]+)\s*:/g, '$1"$2":');
        } else {
            sanitized = "{}";
        }
        data = JSON.parse(sanitized);
    } catch (e) {
        data = {};
    }

    if (!data.ID && !data.id) {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let randomStr = '';
        for (let i = 0; i < 6; i++) {
            randomStr += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        data.ID = `DOC-${randomStr}`;
        isModified = true;
    }

    return isModified ? JSON.stringify(data, null, 2) : null;
};

type Props = {
  fileId: string;
  readOnly?: boolean;
  token?: string;
  apiUrl?: string;
};

export type DiagramEditorHandle = {
  resizeCanvas: () => void;
};

const SAMPLE_XML = `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL"
                  xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"
                  xmlns:dc="http://www.omg.org/spec/DD/20100524/DC"
                  id="Definitions_1">
  <bpmn:process id="Process_1" isExecutable="false">
    <bpmn:startEvent id="StartEvent_1" />
    <bpmn:endEvent id="EndEvent_1" />
  </bpmn:process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="Process_1"/>
  </bpmndi:BPMNDiagram>
</bpmn:definitions>`;

const DiagramEditor = forwardRef<DiagramEditorHandle, Props>(function DiagramEditor(
  { fileId, readOnly = false, token = "", apiUrl = "" },
  ref
) {
  const dialogs = useDialogs();
  const realtime = useRealtime();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const modelerRef = useRef<any | null>(null);
  const [dirty, setDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Set when someone else saves this diagram while we are watching it, so the
  // stale copy on screen is never silently trusted.
  const [staleFrom, setStaleFrom] = useState<string | null>(null);

  /* --- Realtime: presence + advisory lock -------------------------------- */

  const { watch, requestLock, releaseLock, takeover, lockedByOther } = realtime;
  const canEdit = !readOnly;

  // Announce which diagram this client is on, and claim the edit lock when we
  // are allowed to write. Leaving the diagram releases both.
  useEffect(() => {
    if (!fileId) return;
    watch(fileId);
    if (canEdit) requestLock();
    return () => {
      releaseLock();
      watch(null);
    };
  }, [fileId, canEdit, watch, requestLock, releaseLock]);

  // Someone else saved this diagram. If we hold the lock the save is our own
  // echo, so only flag it when we are the read-only party.
  useEffect(() => {
    return realtime.onDiagramSaved((path, by) => {
      if (path !== fileId) return;
      if (!lockedByOther) return;
      setStaleFrom(by ?? "another user");
    });
  }, [realtime, fileId, lockedByOther]);

  const handleTakeover = async () => {
    const holder = realtime.lock?.username ?? "the current editor";
    const confirmed = await dialogs.confirm({
      title: `Take over editing from ${holder}?`,
      description:
        "They will be switched to read-only. Any unsaved changes they have will be lost when they reload.",
      confirmLabel: "Take over",
      tone: "danger",
    });
    if (!confirmed) return;
    takeover();
    // Their last save may have landed after we loaded; reload to be sure.
    window.location.reload();
  };

  // A lock held by someone else is as binding as a missing permission.
  const effectiveReadOnly = readOnly || lockedByOther;

  // --- Documentation & Metadata State ---
  const [docText, setDocText] = useState("");
  const [docOwner, setDocOwner] = useState("");
  const [docParent, setDocParent] = useState("");
  const [showRawDoc, setShowRawDoc] = useState(false);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);

  // --- Sync Approval State ---
  const [approvalStatus, setApprovalStatus] = useState<string | null>(null);

  const resizeNow = () => {
    const m = modelerRef.current;
    if (!m) return;
    try {
      (m.get("canvas") as Canvas).resized();
    } catch(e) { /* Ignore resize errors during unmount */ }
  };

  function getProcessBusinessObject() {
    if (!modelerRef.current) return null;
    try {
      const canvas: any = modelerRef.current.get("canvas");
      const root = canvas.getRootElement();
      if (!root) return null;
      if (root.type === "bpmn:Process") return root.businessObject;
      
      if (root.type === "bpmn:Collaboration") {
        const defs = root.businessObject.$parent;
        if (defs && defs.rootElements) {
          const proc = defs.rootElements.find((e: any) => e.$type === "bpmn:Process");
          if (proc) return proc;
        }
      }
      return root.businessObject;
    } catch (err) {
      return null;
    }
  }

  function getSelectedElement() {
    if (!modelerRef.current || !selectedElementId) return null;
    try {
      const elementRegistry: any = modelerRef.current.get("elementRegistry");
      return elementRegistry.get(selectedElementId) || null;
    } catch (err) {
      return null;
    }
  }

  function getElementDocText(elOrBo: any): string {
    if (!elOrBo) return "";
    try {
      const bo = elOrBo.businessObject || elOrBo;
      const docs = bo.documentation || [];
      return docs.length ? (docs[0].text || "") : "";
    } catch (err) {
      return "";
    }
  }

  function setElementDocText(elOrBo: any, text: string) {
    if (!modelerRef.current || !elOrBo) return;
    try {
      const moddle = modelerRef.current.get("moddle");
      const modeling = modelerRef.current.get("modeling");
      const elementRegistry = modelerRef.current.get("elementRegistry");
      
      const newDoc = text ? [moddle.create("bpmn:Documentation", { text })] : undefined;

      let shape = null;
      if (elOrBo.businessObject) {
        shape = elOrBo; 
      } else if (elOrBo.id) {
        shape = elementRegistry.get(elOrBo.id); 
      }

      // [FIX] Safely fallback to direct businessObject mutation if targeting the root process
      if (shape) {
        modeling.updateProperties(shape, { documentation: newDoc });
      } else {
        const bo = elOrBo.businessObject || elOrBo;
        bo.documentation = newDoc;
        setDirty(true);
      }
    } catch (err) {
      console.error("Failed to update doc text:", err);
    }
  }

  function escapeAttr(s: string) {
    return String(s).replace(/"/g, "&quot;").replace(/\n/g, " ");
  }
  
  function firstUrl(s: string): string | null {
    const m = String(s).match(/https?:\/\/[^\s"'()<>]+/i);
    return m ? m[0] : null;
  }

  function updateNoteOverlayForSelection() {
    if (!modelerRef.current) return;
    try {
      const overlays: any = modelerRef.current.get("overlays");
      const el = getSelectedElement();

      overlays.clear();
      if (!el) return;

      const text = getElementDocText(el);
      if (!text) return;

      const url = firstUrl(text);
      const tooltip = escapeAttr(text);

      // Styling lives in index.css (.schemunda-note-overlay) so the marker
      // tracks the app's design tokens instead of hard-coded hex values.
      const html = url
        ? `<a class="schemunda-note-overlay" href="${url}" target="_blank" rel="noreferrer" title="${tooltip}">Note</a>`
        : `<div class="schemunda-note-overlay" title="${tooltip}">Note</div>`;

      const position = { top: -36, left: (el.width || 0) + 12 } as const;
      overlays.add(el, { position, html });
    } catch (err) {
      // Ignore
    }
  }

  function syncProcessMetadataStates(rawDoc: string) {
    setDocText(rawDoc);
    try {
      if (rawDoc.trim().startsWith('{')) {
        const sanitizedJson = rawDoc.replace(/([{,]\s*)([A-Za-z0-9_]+)\s*:/g, '$1"$2":');
        const parsed = JSON.parse(sanitizedJson);
        setDocOwner(parsed.Owner || parsed.owner || "");
        setDocParent(parsed.Parent || parsed.parent || "");
        return;
      }
    } catch {}
    setDocOwner("");
    setDocParent("");
  }

  useEffect(() => {
    if (!fileId || !token) return;
    const fetchStatus = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/approvals/status?path=${encodeURIComponent(fileId)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setApprovalStatus(data.status);
        } else {
          setApprovalStatus(null);
        }
      } catch (e) {
        setApprovalStatus(null);
      }
    };
    fetchStatus();
  }, [fileId, token, apiUrl]);

  const handleRequestSync = async () => {
    try {
      const res = await fetch(`${apiUrl}/api/approvals/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ path: fileId })
      });

      if (!res.ok) throw new Error("The server rejected the approval request.");

      const data = await res.json();
      setApprovalStatus(data.status);

      if (data.status === "PENDING") {
        await dialogs.notify({
          title: "Sync request submitted",
          description:
            "An approver will review this diagram. You will be notified of the decision by email."
        });
      } else if (data.status === "AUTOAPPROVED") {
        await dialogs.notify({
          title: "Approved automatically",
          description: "This diagram is now eligible for Azure DevOps synchronization."
        });
      }
    } catch (error) {
      await dialogs.notify({
        title: "Could not request sync",
        description: error instanceof Error ? error.message : "Please try again.",
        tone: "danger"
      });
    }
  };

  useEffect(() => {
    setDocOwner("");
    setDocParent("");
    setDocText("");
    setSelectedElementId(null);
    setShowRawDoc(false);

    const modeler = new BpmnJS({
      container: containerRef.current!,
      keyboard: { bindTo: document },
      additionalModules: [CreateAppendAnythingModule]
    });
    modelerRef.current = modeler;

    const eventBus: any = modeler.get("eventBus");
    const onSelectionChanged = (e: any) => {
      try {
        const el = e.newSelection?.[0] || null;
        setSelectedElementId(el ? el.id : null);

        if (el) {
          setDocText(getElementDocText(el));
        } else {
          syncProcessMetadataStates(getElementDocText(getProcessBusinessObject()));
        }
        updateNoteOverlayForSelection();
      } catch (err) {}
    };
    eventBus.on("selection.changed", onSelectionChanged);

    // [FIX] Uses setElementDocText directly so it works on both the root canvas and elements
    const injectIdIfMissing = () => {
        if (readOnly) return null; 
        try {
            const procBo = getProcessBusinessObject();
            if (!procBo) return null;
            
            const currentRaw = getElementDocText(procBo);
            const updatedDocText = ensureDocumentId(currentRaw);
            
            if (updatedDocText) {
                setElementDocText(procBo, updatedDocText);
                return updatedDocText;
            }
        } catch (e) {
            console.warn("Failed to inject document ID", e);
        }
        return null; 
    };

    async function loadXml() {
      if (token) {
        try {
          const res = await fetch(`${apiUrl}/api/files/${fileId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const xml = await res.text();
            await modeler.importXML(xml);
            const injectedText = injectIdIfMissing();
            syncProcessMetadataStates(injectedText || getElementDocText(getProcessBusinessObject()));
            (modeler.get("canvas") as Canvas).resized();
            return;
          }
        } catch (err) {}
      }

      const local = localStorage.getItem(`bpmn:${fileId}`);
      if (local) {
        try {
          await modeler.importXML(local);
          const injectedText = injectIdIfMissing();
          syncProcessMetadataStates(injectedText || getElementDocText(getProcessBusinessObject()));
          (modeler.get("canvas") as Canvas).resized();
          return;
        } catch (err) {}
      }

      try {
        await modeler.importXML(SAMPLE_XML);
        const injectedText = injectIdIfMissing();
        syncProcessMetadataStates(injectedText || getElementDocText(getProcessBusinessObject()));
        (modeler.get("canvas") as Canvas).resized();
      } catch(e) {}
    }

    loadXml();
    modeler.on("commandStack.changed", () => setDirty(true));

    return () => {
      try { eventBus.off("selection.changed", onSelectionChanged); } catch {}
      try { modeler.destroy(); } catch {}
      modelerRef.current = null;
    };
  }, [fileId, readOnly, token, apiUrl]);

  useEffect(() => {
    if (!dirty) return;
    const timer = setTimeout(() => saveXmlAndSvg(), 2000);
    return () => clearTimeout(timer);
  }, [dirty]);

  useImperativeHandle(ref, () => ({ resizeCanvas: resizeNow }));

  useEffect(() => {
    const onWindowResize = () => resizeNow();
    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !(window as any).ResizeObserver) return;
    const ro = new (window as any).ResizeObserver(() => resizeNow());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (selectedElementId || showRawDoc) return;
    if (!modelerRef.current) return;

    const procBo = getProcessBusinessObject();
    if (!procBo) return; 

    const currentRaw = getElementDocText(procBo);
    let parsed: any = {};
    try { 
      const sanitizedJson = currentRaw.replace(/([{,]\s*)([A-Za-z0-9_]+)\s*:/g, '$1"$2":');
      parsed = JSON.parse(sanitizedJson); 
    } catch {}
    if (typeof parsed !== 'object' || parsed === null) parsed = {};

    if (parsed.Parent !== docParent || parsed.Owner !== docOwner) {
      parsed.Parent = docParent;
      parsed.Owner = docOwner;
      
      const newDocText = JSON.stringify(parsed, null, 2);
      setDocText(newDocText);
      setElementDocText(procBo, newDocText);
      setDirty(true);
    }
  }, [docOwner, docParent, selectedElementId, showRawDoc]); 

  function applyDocumentation() {
    if (!modelerRef.current) return;
    const el = getSelectedElement();
    
    if (el) {
      setElementDocText(el, docText);
    } else if (showRawDoc) {
      setElementDocText(getProcessBusinessObject(), docText);
      syncProcessMetadataStates(docText);
    }
    
    setDirty(true);
    updateNoteOverlayForSelection();
  }

  useEffect(() => {
    updateNoteOverlayForSelection();
  }, [selectedElementId, dirty]);

  const COLORS = [
    { name: "white",  fill: "#ffffff" },
    { name: "blue",   fill: "#B3D4FF" },
    { name: "green",  fill: "#C8E6C9" },
    { name: "orange", fill: "#FFE0B2" },
    { name: "red",    fill: "#FFCDD2" },
    { name: "purple", fill: "#E1BEE7" },
  ];

  function setSelectedElementColor(fillHex: string | null) {
    if (!modelerRef.current || !selectedElementId) return;
    try {
      const elementRegistry: any = modelerRef.current.get("elementRegistry");
      const modeling: any = modelerRef.current.get("modeling");
      const el = elementRegistry.get(selectedElementId);
      if (!el) return;

      if (fillHex === null) {
        modeling.setColor(el, { fill: null, stroke: null });
      } else if (el.waypoints) {
        modeling.setColor(el, { stroke: fillHex });
      } else {
        modeling.setColor(el, { fill: fillHex, stroke: "#000000" });
      }
      setDirty(true);
    } catch (e) {}
  }

  async function saveXmlAndSvg() {
    if (!modelerRef.current || effectiveReadOnly) return;
    setIsSaving(true);
    try {
      const { xml } = await modelerRef.current.saveXML({ format: true });
      const { svg } = await modelerRef.current.saveSVG();
      localStorage.setItem(`bpmn:${fileId}`, xml);
      localStorage.setItem(`bpmn:${fileId}:svg`, svg);

      if (token) {
        const res = await fetch(`${apiUrl}/api/files/${fileId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/xml", Authorization: `Bearer ${token}` },
          body: xml
        });

        // 423 Locked: someone claimed the lock between our check and this write.
        // The work is safe in localStorage above, so say so rather than losing it.
        if (res.status === 423) {
          const data = await res.json().catch(() => ({}));
          setIsSaving(false);
          await dialogs.notify({
            title: "This diagram is locked",
            description: `${data.lockedBy || "Someone else"} started editing it. Your changes are kept in this browser -- take over editing to save them.`,
            tone: "danger",
          });
          return;
        }
        if (!res.ok) throw new Error(`Save failed with status ${res.status}`);
      }

      try {
        const res = await fetch(`${apiUrl}/api/approvals/status?path=${encodeURIComponent(fileId)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setApprovalStatus(data.status); 
        } else {
          setApprovalStatus(null);
        }
      } catch (e) {
        setApprovalStatus(null);
      }
      
      setDirty(false);
    } catch (err) {
      console.error("save failed", err);
    } finally {
      setIsSaving(false);
    }
  }

  async function downloadSvg() {
    if (!modelerRef.current) return;
    try {
      const { svg } = await modelerRef.current.saveSVG();
      saveAs(new Blob([svg], { type: "image/svg+xml" }), `${fileId}.svg`);
    } catch(e) {}
  }

  async function downloadXml() {
    if (!modelerRef.current) return;
    try {
      const { xml } = await modelerRef.current.saveXML({ format: true });
      saveAs(new Blob([xml], { type: "application/xml" }), `${fileId}.bpmn`);
    } catch(e) {}
  }

  /* --- Derived toolbar state ------------------------------------------- */

  // Azure DevOps sync requires a real Owner and Parent on the process before an
  // approval can even be requested.
  const safeOwner = (docOwner || "").trim().toLowerCase();
  const safeParent = (docParent || "").trim().toLowerCase();
  const hasValidMetadata =
    safeOwner !== "" &&
    safeParent !== "" &&
    safeOwner !== "unassigned" &&
    safeOwner !== "error";

  const syncState = (() => {
    if (approvalStatus === "PENDING") {
      return {
        variant: "warning" as const,
        label: "Sync pending",
        tooltip: "The metadata is being reviewed.",
        locked: true,
      };
    }
    if (approvalStatus === "APPROVED" || approvalStatus === "AUTOAPPROVED") {
      return {
        variant: "success" as const,
        label: approvalStatus === "AUTOAPPROVED" ? "Auto-approved" : "Sync approved",
        tooltip: "This diagram is eligible for Azure DevOps synchronization.",
        locked: true,
      };
    }
    if (approvalStatus === "DENIED") {
      return {
        variant: "danger" as const,
        label: "Sync denied",
        tooltip: "Your sync request was denied. Modify and save to request again.",
        locked: false,
      };
    }
    return {
      variant: "secondary" as const,
      label: "Request sync",
      tooltip: !hasValidMetadata
        ? "An Owner and a Parent Process are required before requesting sync."
        : dirty || isSaving
          ? "Wait for changes to save before requesting sync."
          : "Request approval to sync this diagram to Azure DevOps.",
      locked: false,
    };
  })();

  const saveStatus = isSaving
    ? { label: "Saving", tone: "text-ink-subtle" }
    : dirty
      ? { label: "Unsaved changes", tone: "text-warning" }
      : { label: "All changes saved", tone: "text-ink-subtle" };

  const inputClass = cn(
    "border-line bg-surface text-ink placeholder:text-ink-subtle",
    "h-8 rounded-lg border px-2.5 text-[13px]",
    "transition-colors duration-(--duration-instant) ease-(--ease-standard)",
    "focus:border-accent focus:outline-none",
    "read-only:bg-surface-sunken read-only:text-ink-muted",
  );

  /**
   * The toolbar is grouped rather than a single flat row: document actions,
   * then the contextual inspector (process metadata, or the selected element's
   * documentation and colour), then save status pinned to the end. Groups are
   * separated by hairlines so the eye can find a control by region.
   */
  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden">
      <div className="bg-surface border-line z-10 flex w-full shrink-0 flex-wrap items-center gap-1.5 border-b px-2.5 py-2">
        {/* --- Document actions -------------------------------------------- */}
        <Button
          variant="primary"
          onClick={saveXmlAndSvg}
          disabled={!dirty || isSaving || effectiveReadOnly}
          title={
            readOnly
              ? "You have read-only access to this diagram"
              : lockedByOther
                ? `${realtime.lock?.username ?? "Someone else"} is editing this diagram`
                : "Save now"
          }
        >
          <CloudArrowUp size={14} />
          Save
        </Button>

        <Button
          variant={syncState.variant}
          disabled={
            !hasValidMetadata || dirty || isSaving || syncState.locked || effectiveReadOnly
          }
          title={syncState.tooltip}
          onClick={handleRequestSync}
        >
          <CloudUpload size={14} />
          {syncState.label}
        </Button>

        <div aria-hidden="true" className="bg-line mx-1 h-5 w-px" />

        <Button onClick={downloadSvg} title="Download as SVG image">
          <Download size={13} />
          SVG
        </Button>
        <Button onClick={downloadXml} title="Download as BPMN XML">
          <Download size={13} />
          XML
        </Button>

        <div aria-hidden="true" className="bg-line mx-1 h-5 w-px" />

        {/* --- Inspector: process metadata (nothing selected) --------------- */}
        {!selectedElementId && !showRawDoc && (
          <div className="animate-fade-in flex flex-wrap items-center gap-1.5">
            <input
              value={docOwner}
              onChange={(e) => setDocOwner(e.target.value)}
              readOnly={effectiveReadOnly}
              placeholder="owner@domain.com"
              aria-label="Process owner"
              title="Process owner — required before requesting sync"
              className={cn(inputClass, "w-52")}
            />
            <input
              value={docParent}
              onChange={(e) => setDocParent(e.target.value)}
              readOnly={effectiveReadOnly}
              placeholder="Parent process"
              aria-label="Parent process"
              title="Parent process — required before requesting sync"
              className={cn(inputClass, "w-44")}
            />
          </div>
        )}

        {/* --- Inspector: documentation / raw metadata ---------------------- */}
        {(selectedElementId || showRawDoc) && (
          <div className="animate-fade-in flex flex-1 flex-wrap items-center gap-1.5">
            {/* Stays a textarea: element documentation and the raw JSON blob
                can both be multi-line, and an <input> would strip newlines. */}
            <textarea
              rows={1}
              value={docText}
              onChange={(e) => setDocText(e.target.value)}
              readOnly={effectiveReadOnly}
              placeholder={
                selectedElementId ? "Element documentation" : "Raw JSON metadata"
              }
              aria-label={
                selectedElementId ? "Element documentation" : "Raw JSON metadata"
              }
              className={cn(
                inputClass,
                "min-w-52 flex-1 resize-y py-1.5 leading-tight",
                !selectedElementId && "font-mono text-xs",
              )}
            />
            <Button onClick={applyDocumentation} disabled={effectiveReadOnly} title="Apply">
              <PencilSquare size={13} />
              Apply
            </Button>
          </div>
        )}

        {!selectedElementId && (
          <Button
            iconOnly
            variant={showRawDoc ? "primary" : "ghost"}
            aria-pressed={showRawDoc}
            onClick={() => setShowRawDoc(!showRawDoc)}
            title="Edit raw JSON metadata"
          >
            <Braces size={14} />
          </Button>
        )}

        {/* --- Inspector: element colour ------------------------------------ */}
        {selectedElementId && !effectiveReadOnly && (
          <div className="animate-fade-in flex items-center gap-1">
            <div aria-hidden="true" className="bg-line mx-1 h-5 w-px" />
            <span className="text-ink-subtle me-0.5 text-[11px] font-semibold tracking-wide uppercase">
              Fill
            </span>
            {COLORS.map((c) => (
              <button
                key={c.name}
                type="button"
                onClick={() => setSelectedElementColor(c.fill)}
                aria-label={`Set fill colour to ${c.name}`}
                title={c.name}
                style={{ background: c.fill }}
                className={cn(
                  "border-line-strong h-5 w-5 rounded-md border",
                  "transition-transform duration-(--duration-instant) ease-(--ease-standard)",
                  "hover:scale-115 active:scale-95",
                )}
              />
            ))}
            <Button
              size="xs"
              variant="ghost"
              onClick={() => setSelectedElementColor(null)}
              title="Reset to default colour"
              className="ms-0.5"
            >
              Reset
            </Button>
          </div>
        )}

        {/* --- Presence ------------------------------------------------------ */}
        <div className="ms-auto flex items-center gap-2.5 ps-2">
          <PresenceAvatars
            viewers={realtime.viewers}
            lock={realtime.lock}
            selfId={realtime.selfId}
          />

          {/* --- Save status ------------------------------------------------- */}
        <div
          role="status"
          aria-live="polite"
          className={cn(
            "flex items-center gap-1.5 text-xs font-medium",
            saveStatus.tone,
          )}
        >
          {isSaving ? (
            <span
              aria-hidden="true"
              className="border-ink-subtle/30 border-t-ink-subtle h-3 w-3 animate-spin rounded-full border-2"
            />
          ) : dirty ? (
            <ExclamationTriangleFill size={11} aria-hidden="true" />
          ) : (
            <CheckCircleFill size={11} aria-hidden="true" />
          )}
          {saveStatus.label}
        </div>
        </div>
      </div>

      {/* --- Lock banner ---------------------------------------------------- */}
      {lockedByOther && (
        <div className="bg-warning-soft border-line animate-fade-in flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b px-3 py-2">
          <ExclamationTriangleFill
            size={13}
            aria-hidden="true"
            className="text-warning shrink-0"
          />
          <p className="text-warning min-w-0 flex-1 text-[13px] font-medium">
            <span className="font-semibold">
              {realtime.lock?.username ?? "Someone else"}
            </span>{" "}
            is editing this diagram. You can look around, but changes cannot be saved.
          </p>
          {!readOnly && (
            <Button variant="warning" onClick={handleTakeover}>
              Take over editing
            </Button>
          )}
        </div>
      )}

      {/* --- Stale copy notice ---------------------------------------------- */}
      {staleFrom && (
        <div className="bg-accent-soft border-line animate-fade-in flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b px-3 py-2">
          <p className="text-accent min-w-0 flex-1 text-[13px] font-medium">
            <span className="font-semibold">{staleFrom}</span> saved a new version.
            What you are looking at is out of date.
          </p>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Reload
          </Button>
          <Button
            variant="ghost"
            onClick={() => setStaleFrom(null)}
            aria-label="Dismiss"
          >
            Dismiss
          </Button>
        </div>
      )}

      <div ref={containerRef} className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden" />
    </div>
  );
});

export default DiagramEditor;